import {DataTypesEnum} from "../enums/dataTypesEnum";
import {BusinessObject} from "./BusinessObject";
import {BusinessObjectWrapperInterface} from "../Interfaces/BusinessObjectWrapperInterface";
import {BusinessObjectInterface} from "../Interfaces/BusinessObjectInterface";
import {RelationshipInterface} from "../Interfaces/RelationshipInterface";
import {Attribute} from "./Attribute";
import {AttributeTypeEnum} from "../enums/AttributeTypeEnum";
import {Relationship} from "./Relationship";
import {AttributeInterface} from "../Interfaces/AttributeInterface";

export class BusinessObjectsWrapper implements BusinessObjectWrapperInterface {
    private readonly dictionary: Map<number, BusinessObject> = new Map();

    constructor(businessObjects: BusinessObject[]) {
        this.addBusinessObjects(businessObjects);
    }

    addBusinessObjects(businessObjects: BusinessObject[]): void {
        businessObjects.forEach(bo => {
            this.dictionary.set(bo.id, bo);
        })
    }

    getBusinessObjects(arg: string): BusinessObjectInterface[] | Map<number, BusinessObjectInterface> {
        switch (arg) {
            case DataTypesEnum.ARRAY: {
                return Array.from(this.dictionary.values());
            }
            case DataTypesEnum.MAP: {
                return this.dictionary;
            }
        }
    }

    getBusinessObject(arg: number | string): BusinessObjectInterface {
        switch (typeof arg) {
            case "number": {
                return this.dictionary.get(arg);
            }
            case "string": {
                const businessObjectsArray = <BusinessObjectInterface[]>this.getBusinessObjects(DataTypesEnum.ARRAY);
                for (let i = 0; i < businessObjectsArray.length; i++) {
                    if (arg.toLowerCase() == businessObjectsArray[i].name.toLowerCase()) {
                        return businessObjectsArray[i];
                    }
                }
            }
        }
    }

    addBusinessObject(businessObject: BusinessObject): void {
        this.dictionary.set(businessObject.id, businessObject);
    }

    updateBusinessObject(businessObject: BusinessObject): void {
        const bo = this.dictionary.get(businessObject.id);
        bo.name = businessObject.name;
        bo.description = businessObject.description;
        bo.setTags(businessObject.getTags());
        bo.addAttributes(businessObject.getAttributes(DataTypesEnum.ARRAY));
        bo.systemRequired = businessObject.systemRequired;
        bo.uniqueName = businessObject.uniqueName;
    }

    deleteBusinessObject(id: number): void {
        this.dictionary.delete(id);
        let businessObjectsArray = <BusinessObjectInterface[]>this.getBusinessObjects(DataTypesEnum.ARRAY);
        businessObjectsArray.forEach(bo => {
            let relArray = <RelationshipInterface[]>bo.getRelationships(DataTypesEnum.ARRAY);
            let relToDelete = relArray.filter(s => s.toBoClass.id == id || s.fromBoClass.id == id);
            if (relToDelete.length > 0) {
                relToDelete.forEach(rel => {
                    let fromBusinessObject = this.getBusinessObject(rel.fromBoClass.id);
                    if (fromBusinessObject != undefined && fromBusinessObject != null) {
                        fromBusinessObject.deleteRelationship(rel.id);
                    }
                    let toBusinessObject = this.getBusinessObject(rel.toBoClass.id);
                    if (toBusinessObject != undefined && toBusinessObject != null) {
                        toBusinessObject.deleteRelationship(rel.id);
                    }
                });
            }
        });
    }


    hasBusinessObject(arg: number | string): boolean {
        switch (typeof arg) {
            case "number": {
                return this.dictionary.has(arg);
            }
            case "string": {
                const businessObjectsArray = <BusinessObjectInterface[]>this.getBusinessObjects(DataTypesEnum.ARRAY);
                for (let i = 0; i < businessObjectsArray.length; i++) {
                    if (businessObjectsArray[i].name.trim().toLowerCase() === arg.trim().toLowerCase()) {
                        return true
                    }
                }
                return false;
            }
        }
    }

    checkBusinessObjectsBeforeSave(currentBusinessObject: BusinessObject): { valid: boolean, data: Map<string, { valid: boolean, msg: string }> } {
        const result = currentBusinessObject.isValid();
        if (this.hasBusinessObject(currentBusinessObject.id)) {
            const businessObject = this.getBusinessObject(currentBusinessObject.id);
            const equal = businessObject.equal(currentBusinessObject);
            result.data.set("equal", {
                valid: !equal,
                msg: equal ? "There are no changes." : null
            })
        }
        if (result.data.get("name").valid) {
            const exists = this.hasBusinessObjectByNameExceptId(currentBusinessObject.name, currentBusinessObject.id);
            result.data.set("name", {
                valid: !exists,
                msg: exists ? `A business object class with name: "${currentBusinessObject.name}" already exists.` : null
            })
        }
        result.valid = !Array.from(result.data.values()).some(item => item.valid === false);
        return result
    }

    getRelationshipsByClassId(id: number): any {
        const businessObject = this.getBusinessObject(id);
        const relationships = <RelationshipInterface[]>businessObject.getRelationships(DataTypesEnum.ARRAY);
        return relationships.map(relationship => {
            if (relationship.toBoClass.id === relationship.fromBoClass.id && relationship.toBoClass.id === id) {
                return {
                    id: relationship.id,
                    relatedClass: relationship.toBoClass,
                    fromBoClass: relationship.fromBoClass,
                    toBoClass: relationship.toBoClass,
                    relationshipType: relationship.inward,
                }
            } else if (relationship.toBoClass.id === id) {
                return {
                    id: relationship.id,
                    relatedClass: relationship.fromBoClass,
                    fromBoClass: relationship.fromBoClass,
                    toBoClass: relationship.toBoClass,
                    relationshipType: relationship.outward
                }
            } else if (relationship.fromBoClass.id === id) {
                return {
                    id: relationship.id,
                    relatedClass: relationship.toBoClass,
                    fromBoClass: relationship.fromBoClass,
                    toBoClass: relationship.toBoClass,
                    relationshipType: relationship.inward
                }
            }
        })
    }

    updateBusinessObjectsAndRelationships(created: { boc: BusinessObjectInterface[], relType: RelationshipInterface[] },
                                          updated: { boc: BusinessObjectInterface[], relType: RelationshipInterface[] },
                                          deleted: { boc: number[], relType: RelationshipInterface[] }): void {
        created.boc.forEach(boc => {
            this.addBusinessObject(new BusinessObject(boc.id, boc.name, boc.description, 0, [], [new Attribute(-2, "Name", "The name of the entry.", "n/a", AttributeTypeEnum.TEXT, -1, -1, -1, -1, ".*", "main", "n/a", "n/a", false,{
                id: boc.id,
                name: boc.name,
                description: boc.description
            }, [], true, true, "n/a", null, false, null)], [], boc.systemRequired, boc.inferenceTemp));
        });

        updated.boc.forEach(boc => {
            this.updateBusinessObject(new BusinessObject(boc.id, boc.name, boc.description, 0, [], [new Attribute(-2, "Name", "The name of the entry.", "n/a", AttributeTypeEnum.TEXT, -1, -1, -1, -1, ".*", "main", "n/a", "n/a", true,{
                id: boc.id,
                name: boc.name,
                description: boc.description
            }, [], true, true, "n/a", null, false, null)], [], boc.systemRequired, boc.inferenceTemp));
        });

        deleted.boc.forEach(id => {
            this.deleteBusinessObject(id);
        });

        created.relType.forEach(rel => {
            let bo = this.getBusinessObject(rel.fromBoClass.id);
            bo.addRelationship(new Relationship(rel.id, rel.name, rel.inward, rel.outward, rel.fromBoClass, rel.toBoClass, rel.systemRequired));
        });

        updated.relType.forEach(rel => {
            if (this.hasBusinessObject(rel.fromBoClass.id)) {
                const bo = this.getBusinessObject(rel.fromBoClass.id);
                bo.deleteRelationship(rel.id);
                bo.addRelationship(new Relationship(rel.id, rel.name, rel.inward, rel.outward, rel.fromBoClass, rel.toBoClass, rel.systemRequired))
            }
        });

        deleted.relType.forEach(rel => {
            if (this.hasBusinessObject(rel.fromBoClass.id)) {
                const bo = this.getBusinessObject(rel.fromBoClass.id);
                bo.deleteRelationship(rel.id);
            }
            if (this.hasBusinessObject(rel.toBoClass.id)) {
                const toBo = this.getBusinessObject(rel.toBoClass.id);
                toBo.deleteRelationship(rel.id);
            }
        });
    }

    hasBusinessObjectByNameExceptId(name: string, id: number): boolean {
        const businessObjectsArray = <BusinessObjectInterface[]>this.getBusinessObjects(DataTypesEnum.ARRAY);
        for (let i = 0; i < businessObjectsArray.length; i++) {
            if (businessObjectsArray[i].name.trim().toLowerCase() === name.trim().toLowerCase() && businessObjectsArray[i].id !== id) {
                return true
            }
        }
        return false;
    }

    getAttributeById(id: number): AttributeInterface {
        const businessObjects = Array.from(this.dictionary.values());
        for (let i = 0; i < businessObjects.length; i++) {
            const attributes = businessObjects[i].attributes;
            if (attributes.has(id)) {
                return attributes.get(id);
            }
        }
        return null;
    }
}



// WEBPACK FOOTER //
// ./app/components/bo-core/models/BusinessObjectsWrapper.ts